import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowUpRight, X } from "lucide-react";

/* Breadcrumbs for inner pages. items: [{label, href}] (last is current). */
export const Breadcrumbs = ({ items = [] }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-6">
    <ol className="flex flex-wrap items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/50">
      {items.map((it, i) => (
        <li key={it.label} className="flex items-center gap-2">
          {i > 0 && <span aria-hidden className="text-white/30">/</span>}
          {it.href ? (
            <Link to={it.href} className="transition-colors hover:text-[#5f8f10]">{it.label}</Link>
          ) : (
            <span className="text-[#5f8f10]">{it.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

/* Contextual cross-links to other pages, shown before the footer. */
export const NextSteps = ({ links = [] }) => (
  <section data-testid="next-steps" className="border-t border-black/10 bg-[#F7F6F2]">
    <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-24 lg:px-16">
      <p className="font-body text-sm font-semibold uppercase tracking-[0.18em] text-[#5f8f10]">Keep exploring</p>
      <div className="mt-8 grid gap-px border border-black/15 bg-black/15 md:grid-cols-3">
        {links.map((l) => (
          <Link key={l.href} to={l.href} data-testid={`nextstep-${l.href}`} className="group flex flex-col bg-[#F7F6F2] p-8 transition-colors duration-200 hover:bg-white">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-black/40">{l.eyebrow}</span>
            <div className="mt-4 flex items-start justify-between gap-4">
              <h3 className="font-display text-2xl uppercase leading-[0.98] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10]">{l.title}</h3>
              <ArrowUpRight strokeWidth={2} className="mt-1 h-6 w-6 shrink-0 text-black/30 transition-all duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#5f8f10]" />
            </div>
            <p className="font-body mt-3 text-base font-normal leading-relaxed text-black/65">{l.blurb}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

/* Ordered site journey for prev/next paging.
   Contact is intentionally excluded; it is a form endpoint, not a read-through page. */
export const PAGE_FLOW = [
  { href: "/what-we-solve", label: "What We Solve" },
  { href: "/how-we-work", label: "How We Work" },
  { href: "/our-work", label: "Our Work" },
  { href: "/about", label: "About" },
  { href: "/insights", label: "Switchboard" },
  { href: "/find-your-switch", label: "Find Your Switch" },
  { href: "/careers", label: "Careers" },
];

/* Previous / Next pager based on the current path's position in PAGE_FLOW.
   Non-wrapping: the first page falls back to Home for "previous", and the
   last page loops to the start of the journey for "next". */
export const Pager = ({ current }) => {
  const idx = PAGE_FLOW.findIndex((p) => p.href === current);
  const prev = idx > 0 ? PAGE_FLOW[idx - 1] : { href: "/", label: "Home" };
  const next = idx >= 0 && idx < PAGE_FLOW.length - 1 ? PAGE_FLOW[idx + 1] : PAGE_FLOW[0];
  return (
    <section data-testid="pager" className="border-t border-white/10 bg-[#161A15]">
      <div className="mx-auto grid max-w-[1360px] grid-cols-1 gap-px bg-white/10 md:grid-cols-2">
        <Link to={prev.href} data-testid="pager-prev" className="group flex flex-col bg-[#161A15] px-6 py-10 md:px-12 md:py-12 lg:px-16">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/45">← Previous</span>
          <span className="font-display mt-3 text-2xl uppercase tracking-tight text-white transition-colors duration-200 group-hover:text-[#5f8f10] md:text-3xl">{prev.label}</span>
        </Link>
        <Link to={next.href} data-testid="pager-next" className="group flex flex-col items-end bg-[#161A15] px-6 py-10 text-right md:px-12 md:py-12 lg:px-16">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/45">Next →</span>
          <span className="font-display mt-3 text-2xl uppercase tracking-tight text-white transition-colors duration-200 group-hover:text-[#5f8f10] md:text-3xl">{next.label}</span>
        </Link>
      </div>
    </section>
  );
};

/* Floating back-to-top arrow, appears after scrolling down. */
export const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      type="button"
      data-testid="back-to-top"
      aria-label="Back to top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 left-6 z-[70] flex h-12 w-12 items-center justify-center border border-white/15 bg-[#161A15] text-white shadow-lg transition-colors duration-200 hover:bg-[#5f8f10] hover:text-[#161A15]"
    >
      <ArrowUp size={20} strokeWidth={2} />
    </button>
  );
};

/* Cookie consent banner, remembers choice in localStorage. */
export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try { setVisible(!localStorage.getItem("switch_cookie_choice")); } catch (e) { setVisible(true); }
  }, []);
  useEffect(() => {
    document.body.classList.toggle("cookie-open", visible);
    return () => document.body.classList.remove("cookie-open");
  }, [visible]);
  const choose = (v) => {
    try { localStorage.setItem("switch_cookie_choice", v); } catch (e) { /* storage unavailable (e.g. private mode) — non-fatal */ }
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-[95] border-t border-white/15 bg-[#161A15] text-white">
      <div className="mx-auto flex max-w-[1360px] flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-12 lg:px-16 lg:pr-28">
        <p className="font-body max-w-2xl text-sm font-normal leading-relaxed text-white/80">
          We use cookies to understand how the site is used and to improve your experience. See our{" "}
          <Link to="/legal#cookies" className="text-[#5f8f10] underline underline-offset-4">Cookie Policy</Link>.
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <button type="button" data-testid="cookie-decline" onClick={() => choose("declined")} className="font-body border border-white/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:border-white">Decline</button>
          <button type="button" data-testid="cookie-accept" onClick={() => choose("accepted")} className="font-body border border-[#5f8f10] bg-[#5f8f10] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#161A15] transition-colors hover:bg-[#4a700c] hover:border-[#4a700c]">Accept</button>
          <button type="button" aria-label="Dismiss" onClick={() => choose("dismissed")} className="flex h-8 w-8 items-center justify-center text-white/50 transition-colors hover:text-white"><X size={18} /></button>
        </div>
      </div>
    </div>
  );
};
